/*
This numberinput field is still quite in progress
I am still unsure how to make sure only numeric input is allowed
For now I choose to ignore all non numeric input by ignoring those keypresses.
This is according to many on the internet not the preferred way because it might surprise the user when it types a unallowed character and nothing happens.
They would argue that the unallowed character should show up in the input and that the input should just show its invalid.
I choose not to do this because this will make the errormessaging to the user more complicated,
but maybe my solution is actually worse since it might surprise the user and actually provides no error message at all
Not sure what is in practice a problem though.
Also some fields will alow floats or negatives others not.
//*/

import React, { useEffect, useRef } from "react";
import { IntlShape, useIntl } from "react-intl";

import inputElementStyles from "../styles/InputElements.module.css";

import styles from "./NumberInput.module.css";

interface Props {
  // changed this from number to string because - and "" should also be permitted intermittedly
  value: string;
  unit: string | JSX.Element;
  // changed this from number to string because - and "" should also be permitted intermittedly
  setValue: (value: string) => void;
  allowDecimals?: boolean;
  allowNegative?: boolean;
  zeroIsInvalid?: boolean;
  // this disabled should probably be removed and instead standard html css functionality should be used
  disabled?: boolean;
  min?: number;
  max?: number;
  required?: boolean;
}

const isValueValid = (
  intl: IntlShape,
  value: string,
  allowDecimals: boolean,
  allowNegative: boolean,
  zeroIsInvalid: boolean,
  min?: number,
  max?: number,
  required?: boolean
) => {
  const newValue = value + "";

  const validMsg = {
    valid: true,
    message: intl.formatMessage({
      id: "number_input.no_problems_with_input",
      defaultMessage: "No problems found",
    }),
  };

  if (!allowDecimals) {
    if (newValue[0] === "0" && newValue.length > 1) {
      return {
        valid: false,
        message: intl.formatMessage({
          id: "number_input.not_start_zero",
          defaultMessage: "Should not start with zero",
        }),
      };
    }
  } else {
    if (newValue[0] === ".") {
      return {
        valid: false,
        message: intl.formatMessage({
          id: "number_input.not_start_dot",
          defaultMessage: "Cannot start with dot",
        }),
      };
    } else if (newValue[0] === "0" && newValue[1] !== "." && newValue.length > 1) {
      return {
        valid: false,
        message: intl.formatMessage({
          id: "number_input.dot_should_follow_zero",
          defaultMessage: "If start with zero then dot should follow zero",
        }),
      };
    } else if (newValue[newValue.length - 1] === ".") {
      return {
        valid: false,
        message: intl.formatMessage({
          id: "number_input.cannot_end_dot",
          defaultMessage: "Cannot end with dot",
        }),
      };
    } else if (newValue.split(".").length - 1 > 1) {
      return {
        valid: false,
        message: intl.formatMessage({
          id: "number_input.too_many_dots",
          defaultMessage: "Too many dots",
        }),
      };
    }
  }
  if (allowNegative) {
    if (newValue.split("-").length - 1 > 1) {
      return {
        valid: false,
        message: intl.formatMessage({
          id: "number_input.too_many_negative_signs",
          defaultMessage: "Too many negative signs",
        }),
      };
    } else if (newValue.indexOf("-") > 0) {
      return {
        valid: false,
        message: intl.formatMessage({
          id: "number_input.negative_signs_should_be_start",
          defaultMessage: "Negative sign should be at start",
        }),
      };
    } else if (newValue === "-") {
      return {
        valid: false,
        message: intl.formatMessage({
          id: "number_input.negative_signs_should_not_be_only_character",
          defaultMessage: "Please add something after the negative sign",
        }),
      };
    }
  }
  if (zeroIsInvalid) {
    if (newValue === "0") {
      return {
        valid: false,
        message: intl.formatMessage({
          id: "number_input.cannot_be_zero",
          defaultMessage: "Value cannot be zero",
        }),
      };
    }
  }
  if (newValue === "") {
    if (!required) {
      return validMsg;
    }
    return {
      valid: false,
      message: intl.formatMessage({
        id: "number_input.value_cannot_be_empty",
        defaultMessage: "Value cannot be empty",
      }),
    };
  }
  if (min && parseFloat(value) < min) {
    return {
      valid: false,
      message: intl.formatMessage({
        id: "number_input.value_too_low",
        defaultMessage: "Value too low",
      }),
    };
  }
  if (max && parseFloat(value) > max) {
    return {
      valid: false,
      message: intl.formatMessage({
        id: "number_input.value_too_high",
        defaultMessage: "Value too high",
      }),
    };
  }

  return validMsg;
};

const NumberInput: React.FC<Props> = (props) => {
  const {
    value,
    unit,
    setValue,
    allowDecimals,
    allowNegative,
    zeroIsInvalid,
    disabled,
    min,
    max,
    required,
  } = props;
  const intl = useIntl();
  const valueValid = isValueValid(
    intl,
    value,
    !!allowDecimals,
    !!allowNegative,
    !!zeroIsInvalid,
    min,
    max
  );
  const myInput = useRef<HTMLInputElement>(null);

  const finalInvalidMessage = !valueValid.valid ? valueValid.message : "";

  useEffect(() => {
    if (myInput && myInput.current) {
      // setCustomValidity("") means input is valid, any other string means invalid with the string as error indication
      myInput.current.setCustomValidity(finalInvalidMessage);
    }
  }, [finalInvalidMessage]);

  return (
    <span className={styles.NumberInput}>
      <input
        title={finalInvalidMessage}
        ref={myInput}
        className={`${inputElementStyles.Padding} ${inputElementStyles.Input} ${
          valueValid.valid ? "" : inputElementStyles.Invalid
        }`}
        // type="number" commented out deliberately willnot work together with pattern
        type="text"
        required={required}
        value={value}
        disabled={disabled}
        // annoyingly it requires javascript to make title dynamic. That means also I have to get to the dom with a ref to see if its valid. Therefore will not do that now
        // title={"Numbers must have pattern of (-)0.00"}
        onKeyPress={(event) => {
          // using caret position failed. Not sure if this is due to react or that the browser caret api just is bogus.
          // START EXPERIMENT CODE
          // try to find complete value or caret position in order to also forbid the minus - if it is not the first character
          // event.persist();
          // const caretPosition = event.target.selectionStart || 0;
          // const value = event.target.value;
          // console.log('keydown',  event);//e.which);
          // const caretPos = window.getSelection().getRangeAt(0).startOffset;
          // console.log('caretPos', caretPos, event.target.value, event.target);
          // END EXPERIMENT CODE

          // only allow legel keypresses to pass
          // this makes sure the onChange is not even called with other keys and thus also the cursor does not change on those keys
          // also backspace delete arrows and tab are still handled
          const possibleChars = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
          if (allowDecimals) possibleChars.push(".");
          // for now no usecase with negatives yet defined
          if (allowNegative) possibleChars.push("-");
          if (!possibleChars.includes(event.key)) {
            if (event.preventDefault) event.preventDefault();
            return false;
          }
        }}
        onChange={(e) => {
          const value = e.target.value;
          let replaced = value;

          // needed for if text is pasted instead of keyed in
          if (allowDecimals && allowNegative) {
            replaced = value.replace(/[^0-9|\-|.]/g, "");
          } else if (allowDecimals && !allowNegative) {
            replaced = value.replace(/[^0-9|.]/g, "");
          } else if (!allowDecimals && allowNegative) {
            replaced = value.replace(/[^0-9|-]/g, "");
          } else if (!allowDecimals && !allowNegative) {
            replaced = value.replace(/[^0-9]/g, "");
          }
          setValue(replaced + "");

          // START EXPERIMENT CODE with carret position. It was not succesfull yet
          // const caretPosition = e.target.selectionStart || 0;
          // e.target.setSelectionRange(caretPosition, caretPosition);

          // if (!allowDecimals) {
          //   const replaceAllExceptNumbers = value.replace(/[^0-9|-]/g, "");
          //   const isNegative = replaceAllExceptNumbers[0] === "-";
          //   const removedMinus = replaceAllExceptNumbers.replace(/[-]/g, "");
          //   const isEmptyString = removedMinus === "";
          //   const removedLeadingZeroes = removedMinus.replace(/^[0]*/,'');
          //   const addOneZeroAgain = removedLeadingZeroes === ""? "0" : removedLeadingZeroes;

          //   const finalValue = `${isNegative? "-": ""}${isEmptyString? "" : addOneZeroAgain}`;
          //   setValue(finalValue);

          //   // if (parseInted || parseInted === 0) {
          //   //   setValue(parseInted + "");
          //   // } else {
          //   //   setValue("");
          //   // }
          // } else { // allowDicimals === true
          //   const allCommasReplaced = value.replace(/,/g, ".");
          //   const replacedAllExceptDigidsAndDots = allCommasReplaced.replace(/[^0-9.]/g, "");

          //   // BEGIN remove all except first dot
          //   const replacedFirstDotWithSpecialSymbol = replacedAllExceptDigidsAndDots.replace(".", "SPECIAL");
          //   const replacedFirstDotWithSpecialSymbolANDRemovedRestOfDots = replacedFirstDotWithSpecialSymbol.replace(/[.]/g, "");
          //   const changedSpecialSymbolBackToDot = replacedFirstDotWithSpecialSymbolANDRemovedRestOfDots.replace("SPECIAL", ".");
          //   const floatingPointString = changedSpecialSymbolBackToDot
          //   // END remove all except first dot

          //   if (floatingPointString === "") {
          //     setValue("");
          //   } else {
          //     const maybeRemovedLeadingZeroes = floatingPointString.replace(/^[0]*/,'');
          //     const maybeAddedZeroAgain = maybeRemovedLeadingZeroes[0] === '.' ||  maybeRemovedLeadingZeroes === "" ? "0" + maybeRemovedLeadingZeroes : maybeRemovedLeadingZeroes;
          //     setValue(maybeAddedZeroAgain + "");
          //   }
          // }
          // END EXPERIMENT CODE
        }}
      />
      <span className={`${styles.Unit} ${inputElementStyles.Padding}`}>{unit}</span>
    </span>
  );
};

export default NumberInput;
